import type { ChatStatus } from 'ai';
import { ArrowUpIcon } from 'lucide-react';
import type { FC, ReactNode, Ref } from 'react';
import { cn } from '../lib/utils';
import { InputGroupAddon } from './input-group';
import { PromptInput, PromptInputSubmit, PromptInputTextarea } from './prompt-input';

interface ChatComposerProps {
  value: string;
  onValueChange: (value: string) => void;
  /** Called with the trimmed draft. The composer clears itself afterwards. */
  onSubmit: (text: string) => void;
  /** `ready` / `error` show send; `submitted` a spinner; `streaming` a stop button. */
  status?: ChatStatus;
  onStop?: () => void;
  placeholder?: string;
  /** Accessible name for the field. Defaults to the placeholder. */
  label?: string;
  /** Keyboard hint beside the send button, e.g. `⌘ J`. */
  shortcut?: ReactNode;
  /** `lg` is the full-screen composer: larger type, roomier field. */
  size?: 'default' | 'lg';
  disabled?: boolean;
  textareaRef?: Ref<HTMLTextAreaElement>;
  className?: string;
}

/**
 * The Assistant's prompt field. Enter sends, Shift+Enter breaks the line, and
 * the field grows with its content up to a cap before it scrolls.
 */
export const ChatComposer: FC<ChatComposerProps> = ({
  value,
  onValueChange,
  onSubmit,
  status = 'ready',
  onStop,
  placeholder = 'Ask a question…',
  label,
  shortcut,
  size = 'default',
  disabled = false,
  textareaRef,
  className
}) => {
  const isBusy = status === 'submitted' || status === 'streaming';
  const canSend = value.trim().length > 0 && !disabled;

  return (
    <PromptInput
      className={className}
      inputGroupClassName={cn(
        'h-auto items-end gap-2.5 rounded-2xl border-border-strong bg-[var(--z-surface-solid)] py-2 pr-2 pl-4 transition-[border-color,box-shadow] duration-150 ease-standard dark:bg-[var(--z-surface-solid)]',
        'has-[[data-slot=input-group-control]:focus-visible]:border-brand-border has-[[data-slot=input-group-control]:focus-visible]:ring-0 has-[[data-slot=input-group-control]:focus-visible]:shadow-brand-sm',
        // InputGroup dims on any disabled descendant, which includes an idle
        // send button. Only a disabled field should dim the composer.
        'has-disabled:bg-[var(--z-surface-solid)] has-disabled:opacity-100 dark:has-disabled:bg-[var(--z-surface-solid)] has-[textarea:disabled]:opacity-60'
      )}
      onSubmit={() => {
        const text = value.trim();
        if (!text || isBusy) {
          return;
        }
        onSubmit(text);
        onValueChange('');
      }}
    >
      <PromptInputTextarea
        ref={textareaRef}
        aria-label={label ?? placeholder}
        value={value}
        onChange={(event) => onValueChange(event.currentTarget.value)}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        className={cn(
          'max-h-40 min-h-[34px] px-0 py-1.5 leading-[1.375rem] text-foreground placeholder:text-muted-foreground',
          size === 'lg'
            ? 'text-body-lg md:text-body-lg'
            : 'text-[0.875rem] md:text-[0.875rem]'
        )}
      />
      <InputGroupAddon align="inline-end" className="mr-0 gap-1.5 py-0 pr-0">
        {shortcut ? (
          <kbd className="px-1 font-sans text-eyebrow font-normal tracking-[0.06em] whitespace-nowrap text-muted-foreground max-sm:hidden">
            {shortcut}
          </kbd>
        ) : null}
        <PromptInputSubmit
          status={status}
          onStop={onStop}
          disabled={isBusy ? !onStop : !canSend}
          aria-label={isBusy ? 'Stop generating' : 'Send message'}
          className="size-[34px] shrink-0 rounded-[10px] bg-primary text-[color:var(--z-on-brand)] shadow-none hover:bg-primary-hover active:bg-primary-active disabled:opacity-40 [&_svg]:size-[15px]"
        >
          {isBusy ? undefined : <ArrowUpIcon strokeWidth={2.4} />}
        </PromptInputSubmit>
      </InputGroupAddon>
    </PromptInput>
  );
};
